/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import BackToView from "components/BackToView/BackToView";
import Preloader from "components/Preloader/Preloader";
import ToastMessage from "components/ToastMessage/ToastMessage";
import Header from "./Header/Header";
import SchedulesWrapper from "./SchedulesWrapper/SchedulesWrapper";
import ElectronicResourceForm from "./Forms/ElectronicResourceForm/ElectronicResourceForm";
import GroupRoomResourceForm from "./Forms/GroupRoomResourceForm/GroupRoomResourceForm";
import DialogResourceForm from "./Forms/DialogResourceForm";
import DialogAlertAction from "components/DialogAlertAction/DialogAlertAction";
import CustomButton from "components/CustomButton/CustomButton";
import ABSOLUTE_PATHS from "routes/utils/absolutePaths";
import { RESOURCES_CONSTANTS } from "config/constants";
import useMainContext from "utils/context/useMainContext";
import useDataRelatedToLibraryResources from "./utils/hooks/useDataRelatedToLibraryResources";
import useServiceList from "./utils/hooks/useServiceList";
import useFilters from "./utils/hooks/useFilters";
import useLoadingRequest from "components/Preloader/utils/hooks/useLoadingRequest";
import usePaginator from "components/Paginator/utils/hooks/usePaginator";
import { USER_ROLES } from "config/userPermissions";
import useDashboardContext from "routes/utils/context/useDashboardContext";
import ResourceHelmet from "../components/ResourceHelmet";
import { RESOURCE_MODULE_TITLES } from "views/Recursos/utils/constants";
import LibraryGuideMessage from "./LibraryGuideMessage";

const ResourcesLibrary = () => {
    const mainContext = useMainContext();
    const { id: userRole } = useDashboardContext().user.role;
    const isLibrarian = userRole === USER_ROLES.RECURSOS_BIBLIOTECARIO;
    //#region States
    const [showDialogResourceForm, setShowDialogResourceForm] = useState(false);
    const [currentResourceTypeIdToForm, setCurrentResourceTypeIdToForm] = useState(null);
    const [currentResourceForUpdate, setCurrentResourceForUpdate] = useState(null);
    const [toastMessage, setToastMessage] = useState({
        message: "-",
        hidden: true
    });
    //#region Custom hooks
    const loadingRequest = useLoadingRequest();
    const { filters } = useFilters({ isLibrarian });
    const paginator = usePaginator();
    const {
        data: {
            resourceTypes,
            campusList,
            libraryList,
            electronicBrands,
            screenList,
            reasonsForPenalize,
            penalties,
            blockList
        },
        fatalError
    } = useDataRelatedToLibraryResources({ loadingRequest });
    const { 
        serviceList, 
        searchedFilters,
        nonBusinessDay
    } = useServiceList({
        filters,
        notifications: {
            setToastMessage,
            loadingRequest
        },
        paginator,
        isLibrarian
    });
    //#endregion
    //#region Effects
    useEffect(() => {
        if (!fatalError) return;
        mainContext.setShowDialogUnexpectedError(true);
    }, [fatalError]);
    useEffect(() => {
        mainContext.reloadForUnexpectedError(fatalError);
    }, [mainContext.showDialogUnexpectedError]);
    //#endregion
    //#region Functions
    const toggleShowDialogServiceForm = (value = null) => {
        setShowDialogResourceForm(prev => value === null ? !prev : value);
    }
    const openShowDialogResourceForm = ({
        resource,
        resourceTypeId
    }) => {
        setShowDialogResourceForm(true);
        let newResource = null;
        if (resource) {
            const { electronic, groupRoom, ...restResource  } = resource;
            if (electronic) {
                newResource = {
                    ...restResource,
                    ...electronic
                };
            }
            else if (groupRoom) {
                newResource = {
                    ...restResource,
                    ...groupRoom
                };
            }
        }
        setCurrentResourceForUpdate(newResource);
        const trustedResourceTypeId = resourceTypeId ? resourceTypeId : searchedFilters.resourceTypeId;
        setCurrentResourceTypeIdToForm(trustedResourceTypeId || 0);
    }
    const renderForm = () => {
        if (!currentResourceTypeIdToForm) 
            return null;
        const genericData = {
            campusList, 
            libraryList, 
            resourceTypes,
        };
        const resourceTypeIdToCreate = {
            value: currentResourceTypeIdToForm,
            set: setCurrentResourceTypeIdToForm
        };
        if (currentResourceTypeIdToForm === RESOURCES_CONSTANTS.RESOURCE_TYPES_IDS.GROUP_ROOM) 
            return (
                <GroupRoomResourceForm 
                    isAdmin={!isLibrarian}
                    currentCampusFilterId={searchedFilters.campusId}
                    currentGroupRoomResourceForUpdate={currentResourceForUpdate}
                    resourceTypeIdToCreate={resourceTypeIdToCreate}
                    show={showDialogResourceForm}
                    toggleShow={toggleShowDialogServiceForm}
                    data={{
                        ...genericData,
                        screenList
                    }}
                    loadingRequest={loadingRequest}
                    fillServiceList={serviceList.fill}
                    filtersAreCompleted={filters.areCompleted}/>
            );
        return (
            <ElectronicResourceForm 
                isAdmin={!isLibrarian}
                campusFilterSelectedId={searchedFilters.campusId}
                resourceTypeIdToCreate={resourceTypeIdToCreate}
                currentElectronicResourceForUpdate={currentResourceForUpdate}
                show={showDialogResourceForm}
                toggleShow={toggleShowDialogServiceForm}
                data={{
                    ...genericData,
                    electronicBrands
                }}
                loadingRequest={loadingRequest}
                fillServiceList={serviceList.fill}
                filtersAreCompleted={filters.areCompleted}/>
        );
    }
    const resetUI = () => {
        filters.clear();
        serviceList.clear(); 
        paginator.clear();
    }
    if (fatalError) return null;
    //#endregion
    const allFilterFieldsWereFilled = filters.areCompleted();
    return (
        <>
        <ResourceHelmet title={RESOURCE_MODULE_TITLES.LIBRARY}/>
        <ToastMessage 
            {...toastMessage}
            toggleHidden={() => setToastMessage(prev => ({ ...prev, hidden: true }))}/>
        <Preloader {...loadingRequest.value}/>
        <BackToView 
            to={ABSOLUTE_PATHS.RESOURCES}
            text="Volver a Inicio"/>
        <Header 
            isLibrarian={isLibrarian}
            filters={filters}
            data={{
                resourceTypes,
                electronicBrands,
                campusList,
                libraryList
            }}
            paginator={paginator}
            resetUI={resetUI}
            searchedFilters={searchedFilters}
            serviceList={serviceList}
            loadingRequest={loadingRequest}
            allFilterFieldsWereFilled={allFilterFieldsWereFilled}
            openShowDialogResourceForm={openShowDialogResourceForm}/>
        {serviceList.value
        ? <SchedulesWrapper 
            openShowDialogResourceForm={openShowDialogResourceForm}
            serviceList={serviceList}
            isLoading={!loadingRequest.value.hidden}
            searchedFilters={searchedFilters}
            data={{
                campusList,
                libraryList,
                resourceTypes,
                electronicBrands,
                screenList,
                reasonsForPenalize,
                penalties,
                blockList
            }}/>
        : <LibraryGuideMessage isAdmin={!isLibrarian}/>}
        <DialogResourceForm 
            show={showDialogResourceForm}
            toggleShow={toggleShowDialogServiceForm}>
            {renderForm()}
        </DialogResourceForm>
        <DialogAlertAction
            variant="secondary"
            title={`El ${nonBusinessDay.value} fue registrado como un Día no hábil`}
            contentComponent={
                <p className="paragraph center">
                    Recuerda que para realizar una búsqueda de reservas, debes considerar una fecha que cuente con un Horario de atención.
                </p>}
            show={nonBusinessDay.dialog.show}
            toggleShow={nonBusinessDay.dialog.toggle}
            width="700px"
            widthContent="90%">
            <CustomButton 
                text="Cancelar"
                onClick={() => nonBusinessDay.dialog.toggle(false)}/>
        </DialogAlertAction>
        </>
    );
}

export default ResourcesLibrary;